import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './index.css';
import App from './App';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebViewRouteGuard, CombinedWebViewRouteGuard } from './components/WebViewRouter';
import { IntrusionWebViewRouteGuard } from './components/IntrusionWebViewRouter';
import Login from './components/Login';
import KivoCallback from './components/KivoCallback';
import reportWebVitals from './reportWebVitals';
import {
  getKioskHomePath,
  isKioskMode,
  isKioskSupportedUser,
} from './utils/kioskMode';
import { isWebViewAuthBypass } from './utils/webviewAudio';

// Create a Material-UI theme (same as in App.js)
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#40518A',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: 'Poppins, Arial, sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

function KioskLoadingScreen() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      Loading...
    </div>
  );
}

function KioskLoginGate() {
  const { isAuthenticated, permissions, loading } = useAuth();

  if (loading) {
    return <KioskLoadingScreen />;
  }
  if (isAuthenticated && permissions && isKioskSupportedUser(permissions)) {
    return <Navigate to={getKioskHomePath(permissions)} replace />;
  }
  return <Login />;
}

function KioskRootRedirect() {
  const { isAuthenticated, permissions, loading, clearAuth } = useAuth();

  useEffect(() => {
    if (loading || !isAuthenticated || !permissions) return;
    if (!isKioskSupportedUser(permissions)) {
      clearAuth();
    }
  }, [loading, isAuthenticated, permissions, clearAuth]);

  if (loading) {
    return <KioskLoadingScreen />;
  }
  if (isWebViewAuthBypass()) {
    return <Navigate to="/webview" replace />;
  }
  if (!isAuthenticated || !permissions) {
    return <Navigate to="/login" replace />;
  }
  if (!isKioskSupportedUser(permissions)) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={getKioskHomePath(permissions)} replace />;
}

function KioskAppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<KioskLoginGate />} />
      <Route path="/kivo-callback" element={<KivoCallback />} />
      <Route path="/webview" element={<WebViewRouteGuard />} />
      <Route path="/webview/:laneSlug" element={<WebViewRouteGuard />} />
      <Route path="/webview-intrusion" element={<IntrusionWebViewRouteGuard />} />
      <Route path="/webview-intrusion/:laneSlug" element={<IntrusionWebViewRouteGuard />} />
      <Route path="/webview-combined" element={<CombinedWebViewRouteGuard />} />
      <Route path="/" element={<KioskRootRedirect />} />
      <Route path="*" element={<KioskRootRedirect />} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  isKioskMode() ? (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <KioskAppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  ) : (
    <App />
  )
);

reportWebVitals();
